import React, { FC, useEffect, useState, useMemo } from 'react'
import { CustomSelect } from '../../custom-select'
import { SurveyChoiceOption, SurveyQuestionOption } from 'src/services/survey'

type DropDownProps = {
  label?: string
  options: SurveyQuestionOption[]
  value: string | undefined | null
  placeholder?: string
  extendClassName?: string
  translate?: string | null
  icon?: React.ReactNode
  handleResponse: (value: string) => void
  handleSearch: (value: string) => void
  isSearchable?: boolean
  valueRef?: undefined | null | SurveyChoiceOption
}

const DropDownField: FC<DropDownProps> = ({
  label,
  options,
  value,
  placeholder = '',
  extendClassName,
  icon,
  translate = null,
  handleResponse,
  handleSearch,
  valueRef = null,
  isSearchable = false,
}) => {
  const formattedOptions = options.map((opt) => ({
    value: opt.value!,
    label: translate
      ? opt.translations?.[translate as keyof typeof opt.translations] || opt.display
      : opt.cleanDisplay || opt.display,
  }))
  const [searchTerm, setSearchTerm] = useState('')
  const [componentKey, setComponentKey] = useState(0)

  // Force complete remount when moving between questions (when both value and valueRef are cleared)
  useEffect(() => {
    if (value === undefined && valueRef === null) {
      setComponentKey((prev) => prev + 1)
      setSearchTerm('')
    }
  }, [value, valueRef])

  // Compute select values based on current response value or saved valueRef
  const selectValues = useMemo(() => {
    // If there's a saved response (valueRef), use it
    if (valueRef) {
      const selectedValue = {
        label: valueRef.cleanDisplay || valueRef.display || valueRef.value,
        value: valueRef.value,
      }
      return [selectedValue]
    }

    // If there's a current response value, find the matching option
    if (value !== undefined && value !== null && value !== '') {
      const matchingOption = formattedOptions.find((opt) => opt.value === value)
      if (matchingOption) {
        return [matchingOption]
      }
    }

    return []
  }, [valueRef, value, formattedOptions])

  useEffect(() => {
    if (searchTerm) {
      handleSearch(searchTerm)
    }
  }, [searchTerm, handleSearch])

  return (
    <section className={`flex w-full flex-col items-start ${extendClassName ?? ''}`}>
      {label && <span className="mb-1 block text-sm font-medium text-primary">{label}</span>}

      <div className="relative w-full" data-testid="dropdown-field-wrapper">
        <CustomSelect
          key={`dropdown-component-${componentKey}`}
          clearable={true}
          options={formattedOptions}
          values={selectValues as { label: string; value: string }[]}
          placeholder={placeholder}
          onChange={(selectedValues) => {
            if (selectedValues?.length > 0) {
              handleResponse(selectedValues[0].value ?? '')
            } else {
              handleResponse('')
            }
          }}
          searchFn={({ state }) => {
            setSearchTerm(state.search)
            return formattedOptions
          }}
          searchable={isSearchable}
        />

        {icon && (
          <section className="absolute inset-y-0 right-0 flex items-center pr-3">{icon}</section>
        )}
      </div>
    </section>
  )
}

export default DropDownField
